import * as api from "../client";
import type { AuditActor } from "./auditService";

const BASE = "/v1/profiles/company-content-move";

/**
 * Client for the SOFTSUPP-530 company content move endpoints on profiles-api
 * (proxied by staff-bff). After an address-book company merge, profiles.*
 * content stays attached to the dead source company uuid — these endpoints
 * re-point it at the surviving company. Types mirror
 * `models/moveCompanyContent.ts` / `models/validateCompanyContentMove.ts` on
 * the API — validate is a read-only dry run, apply performs the move in one
 * transaction and returns the rollback manifest.
 */

/**
 * Every profiles.* table a move touches, in the API's order. Both responses
 * key `counts` (and apply's `movedIds`) by exactly these strings.
 */
export const MOVE_TABLE_KEYS = [
  "lawyer_profiles",
  "firm_profiles",
  "firm_tab_general",
  "firm_tab_awards",
  "firm_tab_client_testimonials",
  "firm_tab_diversity",
  "firm_tab_international_capabilities",
  "firm_tab_multimedia",
  "firm_tab_notable_cases",
  "firm_tab_partner_perspectives",
  "firm_tab_sponsorship",
  "firm_tab_webinar",
  "firm_tab_video",
  "team_pages",
  "firm_articles",
] as const;

export type MoveTableKey = (typeof MOVE_TABLE_KEYS)[number];

/**
 * Request body shared by validate and apply. Ids are uuids; source must
 * differ from target or the API 400s with VALIDATION_ERROR.
 *
 * The three optional lists all share one presence-vs-emptiness contract:
 * omitted means "no restriction", while present-but-empty (`[]`) is a
 * restriction list that lost its contents and moves ZERO rows — it never
 * escalates to an unrestricted move.
 */
export type CompanyContentMoveRequest = {
  sourceCompanyId: string;
  targetCompanyId: string;
  /** Restrict to these profile groups (lawyer/firm profiles only). */
  profileGroupIds?: string[];
  /** Row ids to leave behind — how conflict "skip" decisions are sent. */
  skipRowIds?: string[];
  /**
   * Restrict to ONLY these row ids. The original client-side undo flow sent
   * the flattened `movedIds` manifest here (with source/target swapped), but
   * the request schema caps this list at 5000 ids — the dedicated
   * `operations/{moveId}/undo` endpoints, which restrict to the manifest
   * SERVER-side, are now the canonical undo path. The client-side flow
   * remains valid only for restricted moves under that cap.
   */
  onlyRowIds?: string[];
  /**
   * Id of the operation this apply UNDOES, so the history links the two
   * operations together. Only needed by the legacy client-side undo flow
   * (swapped ids + `onlyRowIds` manifest) — the dedicated undo endpoints
   * stamp it server-side. Omitted for a normal forward move.
   */
  undoesMoveId?: string;
};

/**
 * Request body for the server-side undo endpoints. Everything else the
 * reversal needs — the source/target swap, the manifest-restricted
 * `onlyRowIds`, the `undoesMoveId` history link — is derived SERVER-side from
 * the stored operation row. That is the point: the manifest never round-trips
 * through the 5000-id-capped request schema, so undo works for operations of
 * any size. `skipRowIds` carries the operator's conflict decisions from the
 * undo dry-run.
 */
export type CompanyContentMoveUndoRequest = {
  /** Row ids to leave behind — the conflict "skip" decisions from the undo dry run. */
  skipRowIds?: string[];
};

/**
 * A moving row that would land on a slot the target already occupies. No
 * database constraint prevents forcing it through — the target just ends up
 * with two live rows in the same slot, and which one the public site serves
 * is undefined. Skip is the safe default.
 */
export type CompanyContentMoveConflict =
  | {
      /** firm_profiles and every firm_tab_* table key a version by (language_code, website). */
      table: MoveTableKey;
      kind: "duplicate_language_website";
      sourceRowId: string;
      /** Every existing TARGET row already occupying this row's slot. */
      targetRowIds: string[];
      languageCode: string | null;
      website: string;
    }
  | {
      /** team_pages has no language_code — its slot is (page_type, website). */
      table: "team_pages";
      kind: "duplicate_page_type_website";
      sourceRowId: string;
      targetRowIds: string[];
      pageType: string;
      website: string;
    };

/**
 * Informational (never blocks): the target already has a lawyer profile
 * linked to the same contact as a moving row. The DB allows the same person
 * profiled at two firms — the operator decides.
 */
export type CompanyContentMoveWarning = {
  table: "lawyer_profiles";
  kind: "duplicate_contact";
  sourceRowId: string;
  targetRowIds: string[];
  contactId: string;
};

export type CompanyContentMoveValidateResponse = {
  /** Rows that WOULD move per table — same predicates apply will use. */
  counts: Record<MoveTableKey, number>;
  totalToMove: number;
  /**
   * address-book confirms source was merged DIRECTLY into target. False for
   * a stale intermediate hop in a longer merge chain (A -> B -> C: validating
   * A -> B is not confirmed, since A's final survivor is C) — see
   * `canonicalTargetId` for what to suggest instead.
   */
  relationshipConfirmed: boolean;
  /**
   * Source is not a live company under its own id (404s, or dereferences to
   * a survivor). This is the expected, healthy state after a merge — not
   * itself a red flag.
   */
  sourceCompanyDeleted: boolean;
  /** Target exists and resolves to itself — i.e. is a live company under its own id. */
  targetCompanyAlive: boolean;
  /**
   * The FINAL survivor of source's merge chain, when source was merged away;
   * null when source was never merged. Suggest this as the move target
   * whenever `relationshipConfirmed` is false but source clearly WAS merged
   * somewhere.
   */
  canonicalTargetId: string | null;
  conflicts: CompanyContentMoveConflict[];
  warnings: CompanyContentMoveWarning[];
};

export type CompanyContentMoveApplyResponse = {
  counts: Record<MoveTableKey, number>;
  /**
   * Ids of the rows actually moved, per table — the ROLLBACK MANIFEST, kept
   * on the stored operation. Undo goes through the dedicated
   * `operations/{moveId}/undo` endpoints, which restrict the reverse move to
   * this manifest server-side.
   */
  movedIds: Record<MoveTableKey, string[]>;
  totalMoved: number;
};

/**
 * One recorded move operation — one /apply call that moved at least one row.
 * The operation-level history behind the per-row audit feed.
 */
export type CompanyContentMoveOperation = {
  id: string;
  sourceCompanyId: string;
  targetCompanyId: string;
  totalMoved: number;
  /** The rollback manifest: exactly which row ids this operation moved, per table. */
  movedIds: Record<MoveTableKey, string[]>;
  skipRowIds: string[] | null;
  onlyRowIds: string[] | null;
  /** Set when this operation was itself an undo — the id of the operation it undid. */
  undoesMoveId: string | null;
  /**
   * Set when this operation was undone by a later operation — server-computed
   * across the whole history, so it is correct regardless of which page either
   * operation is on (finding #9). The inverse link of undoesMoveId.
   */
  undoneByMoveId: string | null;
  actor: AuditActor;
  /** ISO timestamp. */
  createdAt: string;
};

export type CompanyContentMoveOperationsResponse = {
  /** Newest-first. */
  operations: CompanyContentMoveOperation[];
  total: number;
  limit: number;
  offset: number;
};

export type CompanyContentMoveOperationsFilters = {
  /** Max 100; the API defaults to 25. */
  limit?: number;
  offset?: number;
  /** Matches operations where this company was EITHER the source or the target. */
  companyId?: string;
};

export const companyContentMoveService = {
  async validate(request: CompanyContentMoveRequest): Promise<CompanyContentMoveValidateResponse> {
    return api.post<CompanyContentMoveValidateResponse>(`${BASE}/validate`, request);
  },

  async apply(request: CompanyContentMoveRequest): Promise<CompanyContentMoveApplyResponse> {
    return api.post<CompanyContentMoveApplyResponse>(`${BASE}/apply`, request);
  },

  async undoValidate(
    moveId: string,
    request: CompanyContentMoveUndoRequest = {},
  ): Promise<CompanyContentMoveValidateResponse> {
    return api.post<CompanyContentMoveValidateResponse>(
      `${BASE}/operations/${moveId}/undo/validate`,
      request,
    );
  },

  async undo(
    moveId: string,
    request: CompanyContentMoveUndoRequest = {},
  ): Promise<CompanyContentMoveApplyResponse> {
    return api.post<CompanyContentMoveApplyResponse>(`${BASE}/operations/${moveId}/undo`, request);
  },

  async listOperations(
    filters: CompanyContentMoveOperationsFilters = {},
  ): Promise<CompanyContentMoveOperationsResponse> {
    const params = new URLSearchParams();
    if (filters.limit !== undefined) params.append("limit", String(filters.limit));
    if (filters.offset !== undefined) params.append("offset", String(filters.offset));
    if (filters.companyId) params.append("companyId", filters.companyId);
    const query = params.toString() ? `?${params}` : "";
    return api.get<CompanyContentMoveOperationsResponse>(`${BASE}/operations${query}`);
  },
};
